// ══════════════════════════════════════════════════════════════
// PAYDIRT AUTH CONTEXT
//
// Wraps the app. Provides:
//   user        — current user object or null
//   loading     — true while checking auth on mount
//   login()     — POST /api/auth/login
//   register()  — POST /api/auth/register
//   logout()    — POST /api/auth/logout
//   showAuth()  — opens the auth modal from anywhere
//   hideAuth()  — closes it
//   authVisible — whether modal is currently showing
//
// On mount: calls GET /api/auth/me to restore session from cookie.
// On 401:   tries POST /api/auth/refresh once, then gives up.
// ══════════════════════════════════════════════════════════════

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from './api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [authVisible, setAuthVisible] = useState(false);
  const [authError, setAuthError] = useState(null);

  // ── Check session on mount ──
  useEffect(() => {
    checkSession();
  }, []);

  async function checkSession() {
    try {
      const res = await apiFetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  // ── Login ──
  const login = useCallback(async (identifier, password) => {
    setAuthError(null);
    try {
      const res = await apiFetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setAuthError(data.error || 'Login failed');
        return { ok: false, error: data.error, field: data.field };
      }

      setUser(data.user);
      setAuthVisible(false);
      return { ok: true, user: data.user };
    } catch (err) {
      const msg = 'Network error — check your connection';
      setAuthError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  // ── Register ──
  const register = useCallback(async (username, email, password) => {
    setAuthError(null);
    try {
      const res = await apiFetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setAuthError(data.error || 'Registration failed');
        return { ok: false, error: data.error, field: data.field };
      }

      setUser(data.user);
      setAuthVisible(false);
      return { ok: true, user: data.user };
    } catch (err) {
      const msg = 'Network error — check your connection';
      setAuthError(msg);
      return { ok: false, error: msg };
    }
  }, []);

  // ── Logout ──
  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // Even if the call fails, clear local state
    }
    setUser(null);
  }, []);

  // ── Modal controls ──
  const showAuth = useCallback(() => {
    setAuthError(null);
    setAuthVisible(true);
  }, []);

  const hideAuth = useCallback(() => {
    setAuthError(null);
    setAuthVisible(false);
  }, []);

  // ── Refresh user data (call after placing bet, etc.) ──
  const refreshUser = useCallback(async () => {
    try {
      const res = await apiFetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      }
    } catch {
      // Silently fail — user stays as-is
    }
  }, []);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    showAuth,
    hideAuth,
    authVisible,
    authError,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
